import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  TextField,
  InputAdornment,
  Card,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { getAllBackups, downloadBackup } from "../../../Services/BackupAndStorageApi";

const DownloadBackupTap = () => {
  const [backups, setBackups] = useState([]);
  const [groupedBackups, setGroupedBackups] = useState({});
  const [filteredGroupedBackups, setFilteredGroupedBackups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // دالة لاستخراج اسم التانت من اسم الملف
  const extractTenantName = (fileName) => {
    // مثال: db_demo01_2025-10-07_14-57-26.sql.gz → demo01
    const match = fileName.match(/^db_([^_]+)_/);
    return match ? match[1] : "غير معروف";
  };

  // دالة لاستخراج التاريخ والوقت من اسم الملف
  const extractDateTime = (fileName) => {
    // مثال: db_demo01_2025-10-07_14-57-26.sql.gz → 2025-10-07 14:57:26
    const match = fileName.match(/(\d{4}-\d{2}-\d{2})_(\d{2}-\d{2}-\d{2})/);
    if (match) {
      const date = match[1]; // 2025-10-07
      const time = match[2].replace(/-/g, ":"); // 14:57:26
      return `${date} ${time}`;
    }
    return "غير معروف";
  };

  // دالة لتنسيق التاريخ بشكل أفضل (اختياري)
  const formatDateTime = (fileName) => {
    const dateTime = extractDateTime(fileName);
    if (dateTime === "غير معروف") return dateTime;

    try {
      const [datePart, timePart] = dateTime.split(" ");
      return (
        <div>
          <div style={{ fontWeight: 600 }}>{datePart}</div>
          <div style={{ fontSize: "0.85em", color: "#666" }}>{timePart}</div>
        </div>
      );
    } catch {
      return dateTime;
    }
  };

  // دالة لتجميع النسخ الاحتياطية حسب التانت
  const groupBackupsByTenant = (backupsArray) => {
    const grouped = {};
    backupsArray.forEach((backup) => {
      const tenantName = extractTenantName(backup.name);
      if (!grouped[tenantName]) {
        grouped[tenantName] = [];
      }
      grouped[tenantName].push(backup);
    });

    // ترتيب النسخ داخل كل مجموعة حسب التاريخ (الأحدث أولاً)
    Object.keys(grouped).forEach((tenant) => {
      grouped[tenant].sort((a, b) => {
        const dateA = extractDateTime(a.name);
        const dateB = extractDateTime(b.name);
        return dateB.localeCompare(dateA);
      });
    });

    return grouped;
  };

  // دالة لفلترة البيانات حسب البحث والتاريخ
  const filterBackups = (grouped, query, fromDate, toDate) => {
    const hasQuery = query.trim();
    const hasDateFilter = fromDate || toDate;

    if (!hasQuery && !hasDateFilter) {
      return grouped;
    }

    const q = query.toLowerCase();
    const filtered = {};

    Object.entries(grouped).forEach(([tenantName, backupsList]) => {
      // تحقق إذا كان اسم التانت يطابق البحث
      const tenantMatches = hasQuery && tenantName.toLowerCase().includes(q);

      // فلترة النسخ داخل المجموعة
      const matchedBackups = backupsList.filter((backup) => {
        // فلترة النص
        let textMatch = true;
        if (hasQuery) {
          const fileName = backup.name.toLowerCase();
          const dateTime = extractDateTime(backup.name).toLowerCase();
          textMatch = (
            fileName.includes(q) ||
            dateTime.includes(q) ||
            tenantName.toLowerCase().includes(q)
          );
        }

        // فلترة التاريخ
        let dateMatch = true;
        if (hasDateFilter) {
          const backupDateTime = extractDateTime(backup.name);
          if (backupDateTime !== "غير معروف") {
            const backupDate = backupDateTime.split(" ")[0]; // استخراج التاريخ فقط
            
            if (fromDate && backupDate < fromDate) {
              dateMatch = false;
            }
            if (toDate && backupDate > toDate) {
              dateMatch = false;
            }
          } else {
            dateMatch = false;
          }
        }

        return textMatch && dateMatch;
      });

      // إضافة المجموعة إذا كان هناك تطابق
      if (tenantMatches || matchedBackups.length > 0) {
        filtered[tenantName] = tenantMatches ? backupsList : matchedBackups;
      }
    });

    return filtered;
  };

  // معالج البحث
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setFilteredGroupedBackups(filterBackups(groupedBackups, query, dateFrom, dateTo));
  };

  // معالج تغيير التاريخ من
  const handleDateFromChange = (e) => {
    const from = e.target.value;
    setDateFrom(from);
    setFilteredGroupedBackups(filterBackups(groupedBackups, searchQuery, from, dateTo));
  };
  
  // معالج تغيير التاريخ إلى
  const handleDateToChange = (e) => {
    const to = e.target.value;
    setDateTo(to);
    setFilteredGroupedBackups(filterBackups(groupedBackups, searchQuery, dateFrom, to));
  };
  
  // دالة لمسح الفلاتر
  const clearFilters = () => {
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
    setFilteredGroupedBackups(groupedBackups);
  };

  useEffect(() => {
    const fetchBackups = async () => {
      try {
        const response = await getAllBackups();
        if (response.status === "success") {
          const backupArray = Object.entries(response.backups).map(([id, name]) => ({
            id,
            name,
          }));
          setBackups(backupArray);
          const grouped = groupBackupsByTenant(backupArray);
          setGroupedBackups(grouped);
          setFilteredGroupedBackups(grouped);
        } else {
          setError("لم يتم العثور على نسخ احتياطية.");
        }
      } catch (err) {
        console.error("Error fetching backups:", err);
        setError("فشل في جلب النسخ الاحتياطية");
      } finally {
        setLoading(false);
      }
    };
    fetchBackups();
  }, []);

  const handleDownload = async (backupName) => {
    setDownloading(backupName);
    setError(null);
    setSuccessMessage("");

    try {
      await downloadBackup(backupName);
      setSuccessMessage(`تم تنزيل الملف: ${backupName}`);
    } catch (err) {
      console.error("❌ خطأ في تحميل النسخة الاحتياطية:", err);
      setError(err?.message || "فشل في تحميل الملف");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
          تحميل النسخ الاحتياطية
        </Typography>
        <Chip 
          label={`${backups.length} ملف`} 
          color="primary" 
          sx={{ fontSize: '1rem', px: 1 }}
        />
      </Box>
      
      {/* Search Bar */}
      <Card sx={{ p: 2, mb: 3, boxShadow: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <FilterListIcon sx={{ mr: 1, color: '#1976d2' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            البحث والفلترة
          </Typography>
        </Box>
        
        {/* Search Text */}
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="ابحث باسم التانت، التاريخ، أو اسم الملف..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
        />
        
        {/* Date Range Filters */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200 }}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <TextField
              size="small"
              type="date"
              label="من تاريخ"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={handleDateFromChange}
              fullWidth
            />
          </Box>
          
          <TextField
            size="small"
            type="date"
            label="إلى تاريخ"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={handleDateToChange}
            sx={{ flex: 1, minWidth: 200 }}
          />
          
          {/* Clear Filters Button */}
          {(searchQuery || dateFrom || dateTo) && (
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilters}
              sx={{ minWidth: 120 }}
            >
              مسح الفلاتر
            </Button>
          )}
        </Box>
      </Card>

      {/* عرض رسالة نجاح قصيرة */}
      {successMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success" onClose={() => setSuccessMessage("")}>
            {successMessage}
          </Alert>
        </Box>
      )}

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : Object.keys(filteredGroupedBackups).length > 0 ? (
        <Box>
          {Object.entries(filteredGroupedBackups).map(([tenantName, backupsList]) => (
            <Accordion key={tenantName} defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: "#f5f5f5",
                  "&:hover": { backgroundColor: "#e3f2fd" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <FolderIcon color="primary" />
                  <Chip
                    label={tenantName}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                  <Badge
                    badgeContent={backupsList.length}
                    color="secondary"
                    sx={{ ml: "auto", mr: 2 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      نسخة احتياطية
                    </Typography>
                  </Badge>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>الرقم</TableCell>
                        <TableCell>التاريخ والوقت</TableCell>
                        <TableCell>اسم الملف</TableCell>
                        <TableCell>تحميل</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {backupsList.map((backup) => (
                        <TableRow key={backup.id}>
                          <TableCell>{backup.id}</TableCell>
                          <TableCell>{formatDateTime(backup.name)}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              {backup.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleDownload(backup.name)}
                              disabled={downloading === backup.name}
                              startIcon={
                                downloading === backup.name ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : null
                              }
                            >
                              {downloading === backup.name ? "جاري..." : "تحميل"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ) : (
        <Alert severity="info">
          {searchQuery ? "لا توجد نتائج مطابقة للبحث" : "لا توجد نسخ احتياطية متاحة"}
        </Alert>
      )}
    </Box>
  );
};

export default DownloadBackupTap;
